import { differenceInCalendarDays } from 'date-fns'

import {
  clampDietDaysCount,
  DIET_DEFAULT_DAYS_COUNT,
  DIET_MAX_DAYS_COUNT,
  DIET_WEEK_DAYS_COUNT,
  getDietWeeksCount,
} from '@/const'

import type { StoreDiet, StoreDietMealDay } from '@/types/Diet'

export const createEmptyStoreDietMealDay = (hour = '23:30'): StoreDietMealDay => ({
  comments: [],
  dishes_and_ingredients: [],
  hour,
})

export const getDietDaysCount = (diet: StoreDiet | null | undefined): number => {
  const meals = diet?.data.meals
  if (!meals?.length) return DIET_DEFAULT_DAYS_COUNT

  const length = Math.max(...meals.map(meal => meal.days.length), 0)
  if (length < 1) return DIET_DEFAULT_DAYS_COUNT
  return clampDietDaysCount(length)
}

export const resolveMealPlanDaysCount = ({
  diet,
  dietId,
  endDate,
  startDate,
}: {
  diet?: StoreDiet | null
  dietId?: number
  endDate?: Date | null
  startDate?: Date | null
}): number => {
  if (
    startDate instanceof Date &&
    endDate instanceof Date &&
    !Number.isNaN(startDate.getTime()) &&
    !Number.isNaN(endDate.getTime())
  ) {
    const daysFromRange = differenceInCalendarDays(endDate, startDate) + 1
    if (daysFromRange >= 1) {
      return clampDietDaysCount(daysFromRange)
    }
  }

  const matchingDiet = dietId === undefined || diet?.id === dietId ? diet : undefined
  return getDietDaysCount(matchingDiet)
}

export const resizeDietDays = (diet: StoreDiet, daysCount: number): boolean => {
  const targetCount = clampDietDaysCount(daysCount)
  let changed = false

  for (const meal of diet.data.meals) {
    while (meal.days.length < targetCount) {
      meal.days.push(createEmptyStoreDietMealDay(meal.hour))
      changed = true
    }
    if (meal.days.length > targetCount) {
      meal.days.length = targetCount
      changed = true
    }
  }

  return changed
}

/** Align all meal day arrays to the same length (max among meals, capped at 31). Does not pad to a fixed default. */
export const normalizeDietDays = (diet: StoreDiet): boolean => {
  if (diet.data.meals.length === 0) return false

  const maxLength = Math.min(
    DIET_MAX_DAYS_COUNT,
    Math.max(...diet.data.meals.map(meal => meal.days.length), 0),
  )

  if (maxLength < 1) {
    return resizeDietDays(diet, DIET_DEFAULT_DAYS_COUNT)
  }

  return resizeDietDays(diet, maxLength)
}

export const getWeekDayCount = (daysCount: number, week: number) => {
  const startDayIndex = (week - 1) * DIET_WEEK_DAYS_COUNT
  return Math.max(0, Math.min(DIET_WEEK_DAYS_COUNT, daysCount - startDayIndex))
}

export { getDietWeeksCount }
